import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useEffect } from 'react';
import radiologyMasterConfigService from '../../services/radiologyMasterConfigService';
import './RadiologyVersionHistoryDrawer.css';

export default function RadiologyVersionHistoryDrawer({
  templateId,
  templateTitle,
  onClose,
  onRestoreSuccess
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);

  const fetchVersions = async () => {
    if (!templateId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await radiologyMasterConfigService.getTemplateVersions(templateId);
      if (res.success && Array.isArray(res.data)) {
        setVersions(res.data);
        if (res.data.length > 0) {
          setSelectedPreview(res.data[0]);
        }
      } else {
        setVersions([]);
      }
    } catch (err) {
      console.error('Failed to load version history:', err);
      setErrorMsg('Failed to load version snapshots for this template.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [templateId]);

  const handleRestore = async (version) => {
    const reason = window.prompt(
      `Restore template to Version ${version.versionNumber}? Please enter change reason:`,
      `Restored from Version ${version.versionNumber}`
    );
    if (reason === null) return; // cancelled

    setRestoringId(version.versionId);
    try {
      const res = await radiologyMasterConfigService.restoreTemplateVersion(
        templateId,
        version.versionId,
        reason || `Restored from v${version.versionNumber}`
      );
      if (res.success) {
        alert(`Successfully restored to Version ${version.versionNumber}!`);
        if (onRestoreSuccess) {
          onRestoreSuccess(res.data);
        }
        onClose();
      }
    } catch (err) {
      console.error('Failed to restore version:', err);
      alert(err.response?.data?.message || 'Error restoring version snapshot.');
    } finally {
      setRestoringId(null);
    }
  };

  // Strip HTML for summary preview
  return (
    <div className="rad-vh-backdrop" onClick={onClose}>
      <div className="rad-vh-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="rad-vh-header">
          <div>
            <h3>📜 Version History & Audit Trail</h3>
            <p className="rad-vh-subtitle">
              Template: <strong>{templateTitle}</strong> (ID #{templateId})
            </p>
          </div>
          <button type="button" className="rad-vh-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {errorMsg && (
          <div className="rad-vh-alert-error">
            <span>⚠️ {errorMsg}</span>
          </div>
        )}

        <div className="rad-vh-body">
          {/* Versions Timeline List (Left) */}
          <div className="rad-vh-list-col">
            <h4 className="rad-vh-list-heading">Historical Snapshots ({versions.length})</h4>

            {loading ? (
              <div className="rad-vh-loading">Loading version snapshots...</div>
            ) : versions.length === 0 ? (
              <div className="rad-vh-empty">
                No past revision snapshots found. (This template may only have the initial version).
              </div>
            ) : (
              <div className="rad-vh-timeline">
                {versions.map((v, idx) => {
                  const isSelected = selectedPreview?.versionId === v.versionId;
                  const isLatest = idx === 0;

                  return (
                    <div
                      key={v.versionId}
                      className={`rad-vh-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedPreview(v)}
                    >
                      <div className="rad-vh-item-top">
                        <div className="rad-vh-badge-wrap">
                          <span className="rad-vh-version-badge">v{v.versionNumber}</span>
                          {isLatest && <span className="rad-vh-latest-badge">Current Live</span>}
                        </div>
                        <span className="rad-vh-date">
                          {new Date(v.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="rad-vh-reason">
                        {v.changeReason || 'Template revision'}
                      </div>

                      <div className="rad-vh-meta">
                        <span>👤 {v.createdByName || 'Administrator'}</span>
                        <span>•</span>
                        <span>{new Date(v.createdAt).toLocaleTimeString()}</span>
                      </div>

                      <div className="rad-vh-actions-row">
                        {!isLatest && (
                          <button
                            type="button"
                            className="rad-vh-btn-restore"
                            disabled={restoringId === v.versionId}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(v);
                            }}
                          >
                            {restoringId === v.versionId ? 'Restoring...' : '↺ Restore This Version'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Version Content Preview (Right) */}
          <div className="rad-vh-preview-col">
            {selectedPreview ? (
              <div className="rad-vh-preview-card">
                <div className="rad-vh-preview-header">
                  <div>
                    <h4>Version {selectedPreview.versionNumber} Content Snapshot</h4>
                    <span className="rad-vh-preview-sub">
                      Saved {new Date(selectedPreview.createdAt).toLocaleString()} by{' '}
                      {selectedPreview.createdByName || 'Administrator'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="rad-vh-btn-restore-lg"
                    disabled={restoringId === selectedPreview.versionId}
                    onClick={() => handleRestore(selectedPreview)}
                  >
                    ↺ Restore Version {selectedPreview.versionNumber}
                  </button>
                </div>

                <div
                  className="rad-vh-rendered-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedPreview.content || '<em>(Empty Content)</em>') }}
                />
              </div>
            ) : (
              <div className="rad-vh-no-preview">
                Select a version on the left to preview its diagnostic findings and format.
              </div>
            )}
          </div>
        </div>

        <div className="rad-vh-footer">
          <button type="button" className="rad-vh-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
